import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { getImageDimensions } from '@web-stories-wp/media';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
} from '@web-stories-wp/design-system';
import { useStory, useCanvas } from '../../app';
import Moveable from '../../components/moveable';
import InOverlay from '../../components/overlay';
import { Z_INDEX_CANVAS } from '../../constants';
import ScalePanel from '../media/scalePanel';
import Display from './display';
import {
  getTextTextureSettings,
  getTextTextureTransform,
  hasTextTextureDimensions,
} from './utils/getTextTextureTransform';

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
`;
const Image = styled.img`
  position: absolute;
  max-width: none;
  pointer-events: none;
  opacity: 0.25;
`;
const Target = styled.div`
  position: absolute;
  cursor: grab;
  touch-action: none;
  outline: 1px solid ${({ theme }) => theme.colors.border.focus};
  &:active {
    cursor: grabbing;
  }
`;
const Actions = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  width: max-content;
  gap: 12px;
  transform: translateX(-50%);
  button {
    flex: 0 0 auto;
    white-space: nowrap;
  }
  padding: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bg.primary};
`;

export function getPannedTexture(texture, width, height, dx, dy) {
  const bounds = getTextTextureTransform(texture, width, height);
  if (!bounds) return texture;
  const settings = getTextTextureSettings(texture);
  const position = (offset, extent, layer, fallback) =>
    extent === layer
      ? fallback
      : Math.min(1, Math.max(0, offset / (layer - extent)));
  return {
    ...texture,
    position: {
      x: position(bounds.x + dx, bounds.width, width, settings.position.x),
      y: position(bounds.y + dy, bounds.height, height, settings.position.y),
    },
  };
}

export default function TextureEdit({ element, box }) {
  const { clearEditing } = useCanvas(({ actions }) => actions);
  const { updateElementById, currentPageId } = useStory(
    ({ actions, state }) => ({
      updateElementById: actions.updateElementById,
      currentPageId: state.currentPage.id,
    })
  );
  const originPageId = useRef(currentPageId);
  const [texture, setTexture] = useState(null);
  const [error, setError] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const original = useRef(element.textTexture);
  const currentSource = useRef(element.textTexture?.src);
  currentSource.current = element.textTexture?.src;
  const latest = useRef(null);
  const start = useRef(null);
  const moveable = useRef(null);
  const changed = useRef(false);
  const { width, height, x, y } = box;
  const edit = (next) => {
    if (!next) return;
    latest.current = next;
    changed.current = true;
    setTexture(next);
  };
  useEffect(() => {
    let cancelled = false;
    const source = original.current;
    const settings = getTextTextureSettings(source);
    if (!hasTextTextureDimensions(source)) {
      const snap = (v) => (v <= 0 ? 0 : v >= 1 ? 1 : 0.5);
      settings.position = {
        x: snap(settings.position.x),
        y: snap(settings.position.y),
      };
    }
    const prepare = (dimensions) => {
      const next = { ...source, ...dimensions, ...settings };
      if (!hasTextTextureDimensions(next))
        throw new Error('Invalid dimensions');
      if (!cancelled) {
        latest.current = next;
        setTexture(next);
      }
    };
    if (hasTextTextureDimensions(source)) prepare({});
    else
      getImageDimensions(source.src)
        .then(prepare)
        .catch(() => {
          if (!cancelled) setError(true);
        });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (element.textTexture?.src !== original.current.src) clearEditing();
  }, [element.textTexture?.src, clearEditing]);
  const commit = useCallback(() => {
    if (currentSource.current !== original.current.src) return;
    if (!changed.current || !latest.current) return;
    const previous = getTextTextureSettings(original.current);
    const next = getTextTextureSettings(latest.current);
    if (!hasTextTextureDimensions(original.current)) {
      const snap = (v) => (v <= 0 ? 0 : v >= 1 ? 1 : 0.5);
      previous.position = {
        x: snap(previous.position.x),
        y: snap(previous.position.y),
      };
    }
    if (JSON.stringify(previous) !== JSON.stringify(next))
      updateElementById({
        elementId: element.id,
        pageId: originPageId.current,
        properties: { textTexture: latest.current },
      });
  }, [element.id, updateElementById]);
  // Commit before the closed-mode UI can expose Save or template snapshots.
  useLayoutEffect(() => commit, [commit]);
  useEffect(() => {
    target?.focus();
  }, [target]);
  useEffect(() => {
    moveable.current?.updateRect();
  }, [texture, width, height, x, y, box.rotationAngle]);
  useEffect(() => {
    const node = ref.current;
    const wheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (latest.current)
        edit({
          ...latest.current,
          scale: Math.min(
            3,
            Math.max(
              1,
              getTextTextureSettings(latest.current).scale + event.deltaY / 100
            )
          ),
        });
    };
    node.addEventListener('wheel', wheel, { passive: false });
    return () => node.removeEventListener('wheel', wheel);
  }, []);
  const bounds = getTextTextureTransform(texture || undefined, width, height);
  const imageStyle = bounds
    ? {
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
      }
    : null;
  return (
    <Wrapper ref={ref}>
      {bounds && (
        <Image alt="" src={texture.src} style={imageStyle} draggable={false} />
      )}
      <Display
        element={texture ? { ...element, textTexture: texture } : element}
        box={box}
        svgRenderType="edit"
      />
      {bounds && (
        <Target
          ref={setTarget}
          style={imageStyle}
          tabIndex={0}
          role="group"
          aria-label={__(
            'Drag to move texture; use arrow keys to reposition',
            'web-stories'
          )}
          onKeyDown={(event) => {
            const direction = {
              ArrowLeft: [-1, 0],
              ArrowRight: [1, 0],
              ArrowUp: [0, -1],
              ArrowDown: [0, 1],
            }[event.key];
            if (!direction) return;
            event.preventDefault();
            event.stopPropagation();
            const amount = event.shiftKey ? 10 : 1;
            edit(
              getPannedTexture(
                latest.current,
                width,
                height,
                direction[0] * amount,
                direction[1] * amount
              )
            );
          }}
        />
      )}
      {target && (
        <Moveable
          ref={moveable}
          targets={target}
          draggable
          origin={false}
          throttleDrag={0}
          onDragStart={() => {
            start.current = latest.current;
          }}
          onDrag={({ dist }) =>
            edit(
              getPannedTexture(start.current, width, height, dist[0], dist[1])
            )
          }
          onDragEnd={() => target.focus()}
        />
      )}
      {bounds && (
        <ScalePanel
          x={x}
          y={y}
          width={width}
          height={height}
          scale={getTextTextureSettings(texture).scale * 100}
          max={300}
          aria-label={__('Texture scale', 'web-stories')}
          setProperties={({ scale }) =>
            edit({ ...latest.current, scale: scale / 100 })
          }
        />
      )}
      <InOverlay zIndex={Z_INDEX_CANVAS.FLOAT_PANEL} pointerEvents="initial">
        <Actions style={{ left: x + width / 2, top: y + height + 60 }}>
          {error && (
            <span role="alert">
              {__(
                'Could not load the texture. Replace it to adjust it.',
                'web-stories'
              )}
            </span>
          )}
          <Button
            size={BUTTON_SIZES.SMALL}
            type={BUTTON_TYPES.SECONDARY}
            disabled={!bounds}
            onClick={() =>
              edit({
                ...latest.current,
                scale: 1,
                position: { x: 0.5, y: 0.5 },
              })
            }
          >
            {__('Reset texture', 'web-stories')}
          </Button>
          <Button
            size={BUTTON_SIZES.SMALL}
            type={BUTTON_TYPES.PRIMARY}
            onClick={clearEditing}
          >
            {__('Done', 'web-stories')}
          </Button>
        </Actions>
      </InOverlay>
    </Wrapper>
  );
}
TextureEdit.propTypes = {
  element: PropTypes.object.isRequired,
  box: PropTypes.object.isRequired,
};
